export class SliderGroup extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.resizeObserver = null;
    this.lastWidth = 0;
    // Root element for Keen is the .keen-slider child inside <slider-group>
    this.sliderContainer = this.querySelector('.slider-group-slider') || null;
    this.direction = this.dataset.direction === 'true';
    this.slidesLength = this.querySelectorAll('.swiper .swiper-slide').length;
    this.speed = Number(this.dataset.speed) || 300;
    this.sliderPerViewDesktop = Number(this.dataset.sliderPerViewDesktop) || this.querySelectorAll('.swiper .swiper-slide').length;
    this.sliderPerViewTablet = Number(this.dataset.sliderPerViewTablet) || 3;
    this.sliderPerViewMobile = Number(this.dataset.sliderPerViewMobile) || 1.5;
    this.navigation = !!this.querySelector('.slider-group-navigation');
    this.centeredSlides = this.dataset.centeredSlides || 'auto';
    this.gap = Number(this.dataset.gap) || 12;
    this.gapTablet = Number(this.dataset.gapTablet) || this.gap;
    this.gapMobile = Number(this.dataset.gapMobile) || this.gap;
    this.loop = this.dataset.loop === 'true';
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplayDelay = Number(this.dataset.autoplayDelay) || 0;
  }

  connectedCallback() {
    const container = this.sliderContainer;
    if (!container) return;
  
    this.initializeSlider();
    this.setupResizeObserver();
  }

  buildOptions() {
    return {
      loop: this.loop,
      autoplay: this.autoplay ? { delay: this.autoplayDelay, reverseDirection: this.direction } : false,
      slidesPerGroup: 1,
      speed: this.speed,
      navigation: this.navigation ? { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' } : false,
      preloadImages: false,
      allowTouchMove: !(this.autoplay && this.autoplayDelay === 0),
      simulateTouch: !(this.autoplay && this.autoplayDelay === 0),
      grabCursor: !(this.autoplay && this.autoplayDelay === 0),
      breakpoints: {
        0: {
          centeredSlides: this.centeredSlides === 'center',
          centeredSlidesBounds: true,
          slidesPerView: this.sliderPerViewMobile,
          spaceBetween: this.gapMobile,
        },
        750: {
          enabled: this.slidesLength !== this.sliderPerViewTablet,
          centeredSlides: this.slidesLength === this.sliderPerViewTablet,
          centeredSlidesBounds: true,
          slidesPerView: this.sliderPerViewTablet,
          spaceBetween: this.gapTablet,
        },
        1200: {
          enabled: this.slidesLength !== this.sliderPerViewDesktop,
          centeredSlides: this.centeredSlides === 'center',
          centeredSlidesBounds: true,
          slidesPerView: this.sliderPerViewDesktop,
          spaceBetween: this.gap,
        },
      }
    };
  }

  initializeSlider() {
    if (!this.sliderContainer) return;

    // Destroy existing slider if present
    if (this.slider && typeof this.slider.destroy === 'function') {
      this.slider.destroy();
      this.slider = null;
    }

    this.slider = new Swiper(this.sliderContainer, this.buildOptions());
    this.applyAlternatingClasses();
  }

  setupResizeObserver() {
    // Initialize with current width
    this.lastWidth = this.offsetWidth;

    // Create ResizeObserver to watch for width changes
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        
        // Only reinitialize if width actually changed
        if (Math.abs(newWidth - this.lastWidth) > 1) {
          this.lastWidth = newWidth;
          this.initializeSlider();
        }
      }
    });

    this.resizeObserver.observe(this);
  }

  applyAlternatingClasses() {
    const slides = this.querySelectorAll('.swiper-slide');
    
    if (!slides || slides.length === 0) return;
    
    slides.forEach((slide, index) => {
      if (slide instanceof HTMLElement) {
        // Use forEach index (0-based), add 1 to make it 1-based like forloop.index
        const slideIndex = index + 1;
        if (slideIndex % 2 === 1) {
          slide.classList.add('slide-odd');
        } else {
          slide.classList.add('slide-even');
        }
      }
    });
  }


  disconnectedCallback() {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clean up Swiper instance
    if (this.slider && typeof this.slider.destroy === 'function') {
      this.slider.destroy();
      this.slider = null;
    }
  }  
}

if (!customElements.get('slider-group')) {
  customElements.define('slider-group', SliderGroup);
}